import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Smartphone, Wifi, Users, AlertCircle, RefreshCw } from 'lucide-react';
import { serviceAPI } from '../services/api';
import unitelLogo from '../assets/logos/unitellogo.png';
import ltcLogo from '../assets/logos/ltclogo.png';
import tplusLogo from '../assets/logos/tpluslogo.png';
import etlLogo from '../assets/logos/etllogo.png';
import bestLogo from '../assets/logos/bestlogo.png';
import { DEFAULT_REPORT_MONTH, DEFAULT_REPORT_YEAR } from '../utils/reportPeriod';

const logoMap = {
  UNITEL: unitelLogo,
  UNI: unitelLogo,
  LTC: ltcLogo,
  TPLUS: tplusLogo,
  TPL: tplusLogo,
  'T-PLUS': tplusLogo,
  ETL: etlLogo,
  BEST: bestLogo,
  BST: bestLogo,
};

const operatorBrandColors = {
  UNITEL: '#ea580c', // vibrant orange
  UNI: '#ea580c',
  LTC: '#dc2626',    // bold red
  TPLUS: '#d97706',  // golden amber
  TPL: '#d97706',
  'T-PLUS': '#d97706',
  ETL: '#0284c7',    // cyan / sky blue
  BEST: '#2563eb',   // royal blue
  BST: '#2563eb',
};

const getBrandColor = (code, name, fallbackColor) => {
  const codeKey = (code || '').toUpperCase();
  const nameKey = (name || '').toUpperCase();
  return (
    operatorBrandColors[codeKey] ||
    operatorBrandColors[nameKey] ||
    fallbackColor ||
    '#3b82f6'
  );
};

const formatNumber = (n) => {
  if (n === null || n === undefined) return '0';
  return new Intl.NumberFormat('en-US').format(Number(n));
};

export default function OperatorServiceBreakdown({ month, year }) {
  const { t } = useTranslation();
  const [operators, setOperators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const monthParam = month != null ? month : DEFAULT_REPORT_MONTH;
  const yearParam = year != null ? year : DEFAULT_REPORT_YEAR;

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await serviceAPI.getServiceTypeOperators({ month: monthParam, year: yearParam });
      const operatorsData = resp?.data || resp || [];
      const processed = operatorsData.map((r) => ({
        id: r.operator_id,
        name: r.operator_name,
        code: r.operator_code,
        color: r.color,
        logoUrl: r.logo_url,
        mbbSubscribers: parseInt(r.mbb?.total || 0, 10),
        ftbSubscribers: parseInt(r.ftb?.total || 0, 10),
        mbbActive: parseInt(r.mbb?.active || 0, 10),
        ftbActive: parseInt(r.ftb?.active || 0, 10),
      }));
      setOperators(processed);
    } catch (err) {
      console.error('Failed to load operator breakdown:', err);
      setError(err.message || t('operatorBreakdown.loadError'));
    } finally {
      setLoading(false);
    }
  }, [monthParam, yearParam]);

  useEffect(() => {
    let isMounted = true;
    fetchData();
    return () => {
      isMounted = false;
    };
  }, [fetchData]);

  if (loading) {
    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-xl font-bold text-gray-900">{t('operatorBreakdown.title')}</h2>
          <p className="text-sm text-gray-600 mt-0.5">
            {t('operatorBreakdown.subtitle')}
          </p>
        </div>
        <div className="flex flex-col items-center justify-center py-16 bg-white rounded-xl border border-gray-200 shadow-sm">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
          <span className="mt-3 text-sm font-medium text-gray-600">{t('operatorBreakdown.loading')}</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-xl font-bold text-gray-900">{t('operatorBreakdown.title')}</h2>
          <p className="text-sm text-gray-600 mt-0.5">
            {t('operatorBreakdown.subtitle')}
          </p>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-red-700">
          <div className="flex items-start gap-3">
            <AlertCircle className="h-6 w-6 text-red-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="text-base font-semibold text-red-800">{t('operatorBreakdown.errorTitle')}</h3>
              <p className="text-sm text-red-600 mt-1">{error}</p>
              <button
                onClick={fetchData}
                className="mt-4 inline-flex items-center gap-1.5 px-4 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-red-600 hover:bg-red-700 transition-colors shadow-sm"
              >
                <RefreshCw className="h-4 w-4" />
                <span>{t('common.tryAgain')}</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (operators.length === 0) {
    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-xl font-bold text-gray-900">{t('operatorBreakdown.title')}</h2>
          <p className="text-sm text-gray-600 mt-0.5">
            {t('operatorBreakdown.subtitle')}
          </p>
        </div>
        <div className="text-center py-12 bg-white rounded-xl border border-gray-200 text-gray-500 shadow-sm">
          {t('operatorBreakdown.noData')}
        </div>
      </div>
    );
  }

  // Sort descending by total subscribers (MBB + FTB)
  const sortedOperators = [...operators].sort((a, b) => {
    const totalA = (a.mbbSubscribers || 0) + (a.ftbSubscribers || 0);
    const totalB = (b.mbbSubscribers || 0) + (b.ftbSubscribers || 0);
    return totalB - totalA;
  });

  return (
    <div className="space-y-6">
      {/* Section Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <div>
          <h2 className="text-xl font-bold text-gray-900">{t('operatorBreakdown.title')}</h2>
          <p className="text-sm text-gray-600 mt-0.5">
            {t('operatorBreakdown.subtitle')}
          </p>
        </div>
      </div>

      {/* Responsive 2-column grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {sortedOperators.map((op, idx) => {
          const mbb = op.mbbSubscribers || 0;
          const ftb = op.ftbSubscribers || 0;
          const totalSubscribers = mbb + ftb;

          const mbbActive = op.mbbActive || 0;
          const ftbActive = op.ftbActive || 0;
          const totalActive = mbbActive + ftbActive;

          const mbbInactive = Math.max(0, mbb - mbbActive);
          const ftbInactive = Math.max(0, ftb - ftbActive);

          const brandColor = getBrandColor(op.code, op.name, op.color);

          const overallActivePct = totalSubscribers > 0 ? (totalActive / totalSubscribers) * 100 : 0;
          const mbbPct = mbb > 0 ? (mbbActive / mbb) * 100 : 0;
          const ftbPct = ftb > 0 ? (ftbActive / ftb) * 100 : 0;

          // Share of this operator's total customer base
          const mbbShare = totalSubscribers > 0 ? (mbb / totalSubscribers) * 100 : 0;
          const ftbShare = totalSubscribers > 0 ? (ftb / totalSubscribers) * 100 : 0;

          const codeKey = (op.code || '').toUpperCase();
          const nameKey = (op.name || '').toUpperCase();
          const logoSrc = logoMap[codeKey] || logoMap[nameKey] || op.logoUrl;

          return (
            <div
              key={op.id || op.code || idx}
              className="bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-all duration-200 overflow-hidden flex flex-col"
            >
              {/* Brand Accent Top Stripe */}
              <div
                className="h-1.5 w-full"
                style={{ backgroundColor: brandColor }}
              />

              {/* Operator Identity & Total Header */}
              <div className="p-5 pb-4 flex items-center justify-between gap-4 border-b border-gray-100">
                <div className="flex items-center gap-3.5 min-w-0">
                  {/* Logo Container */}
                  <div className="w-12 h-12 rounded-xl bg-gray-50 border border-gray-100 flex items-center justify-center p-1.5 shadow-sm flex-shrink-0">
                    {logoSrc ? (
                      <img
                        src={logoSrc}
                        alt={`${op.name} logo`}
                        className="w-full h-full object-contain"
                      />
                    ) : (
                      <div
                        className="text-base font-extrabold"
                        style={{ color: brandColor }}
                      >
                        {op.name ? op.name.substring(0, 2).toUpperCase() : '??'}
                      </div>
                    )}
                  </div>

                  {/* Name & Badges */}
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="text-base font-bold text-gray-900 truncate">
                        {op.name}
                      </h3>
                      <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 border border-gray-200">
                        {op.code || `#${idx + 1}`}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-xs text-gray-500 font-medium">{t('operatorBreakdown.rank', { value: idx + 1 })}</span>
                      <span className="text-gray-300">•</span>
                      <span className="inline-flex items-center text-xs font-semibold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-md border border-emerald-100">
                        {t('operatorBreakdown.activeOverall', { value: overallActivePct.toFixed(1) })}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Total Count */}
                <div className="text-right flex-shrink-0">
                  <div className="text-xs font-medium text-gray-500 uppercase tracking-wide">{t('common.total')}</div>
                  <div className="text-2xl font-extrabold text-gray-900 leading-tight">
                    {formatNumber(totalSubscribers)}
                  </div>
                </div>
              </div>

              {/* Service Sub-Cards Grid */}
              <div className="p-5 pt-4 grid grid-cols-1 sm:grid-cols-2 gap-4 flex-1">
                {/* MBB Sub-Card */}
                <div className="bg-slate-50/70 border border-slate-200/80 rounded-xl p-4 flex flex-col justify-between">
                  <div>
                    {/* Header */}
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <div className="p-1.5 rounded-lg bg-blue-100 text-blue-700">
                          <Smartphone className="h-4 w-4" />
                        </div>
                        <div>
                          <span className="text-sm font-bold text-gray-900">MBB</span>
                          <span className="text-[11px] text-gray-500 block">{t('operatorBreakdown.mobileBroadband')}</span>
                        </div>
                      </div>
                      <span className="text-[11px] font-semibold px-2 py-0.5 rounded bg-white text-gray-700 border border-gray-200 shadow-xs">
                        {t('operatorBreakdown.base', { value: mbbShare.toFixed(1) })}
                      </span>
                    </div>

                    {/* Metrics */}
                    <div className="flex items-baseline justify-between mb-1">
                      <div className="text-xl font-bold text-gray-900">
                        {formatNumber(mbb)}
                      </div>
                      <div className="text-xs font-bold text-emerald-600">
                        {t('operatorBreakdown.activeSuffix', { value: formatNumber(mbbActive) })}
                      </div>
                    </div>
                  </div>

                  {/* Progress & Inactive */}
                  <div className="mt-3">
                    <div className="flex items-center justify-between text-xs text-gray-500 mb-1.5 font-medium">
                      <span>{t('operatorBreakdown.activeRate')}</span>
                      <span className="font-bold text-gray-700">{mbb > 0 ? `${mbbPct.toFixed(1)}%` : '—'}</span>
                    </div>
                    {/* Dual-tone progress bar */}
                    <div className="w-full bg-gray-200 rounded-full h-2.5 overflow-hidden flex">
                      <div
                        className="h-full rounded-full transition-all duration-500"
                        style={{
                          width: `${mbbPct}%`,
                          backgroundColor: brandColor,
                        }}
                      />
                    </div>
                    <div className="mt-2 flex items-center justify-between text-[11px] text-gray-500">
                      <span>{t('operatorBreakdown.inactive')}</span>
                      <span className="font-semibold text-gray-700">{formatNumber(mbbInactive)}</span>
                    </div>
                  </div>
                </div>

                {/* FTB Sub-Card */}
                <div className="bg-slate-50/70 border border-slate-200/80 rounded-xl p-4 flex flex-col justify-between">
                  <div>
                    {/* Header */}
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <div className="p-1.5 rounded-lg bg-indigo-100 text-indigo-700">
                          <Wifi className="h-4 w-4" />
                        </div>
                        <div>
                          <span className="text-sm font-bold text-gray-900">FTB</span>
                          <span className="text-[11px] text-gray-500 block">{t('operatorBreakdown.fixedBroadband')}</span>
                        </div>
                      </div>
                      <span className="text-[11px] font-semibold px-2 py-0.5 rounded bg-white text-gray-700 border border-gray-200 shadow-xs">
                        {t('operatorBreakdown.base', { value: ftbShare.toFixed(1) })}
                      </span>
                    </div>

                    {/* Metrics */}
                    <div className="flex items-baseline justify-between mb-1">
                      <div className="text-xl font-bold text-gray-900">
                        {formatNumber(ftb)}
                      </div>
                      <div className="text-xs font-bold text-emerald-600">
                        {t('operatorBreakdown.activeSuffix', { value: formatNumber(ftbActive) })}
                      </div>
                    </div>
                  </div>

                  {/* Progress & Inactive */}
                  <div className="mt-3">
                    <div className="flex items-center justify-between text-xs text-gray-500 mb-1.5 font-medium">
                      <span>{t('operatorBreakdown.activeRate')}</span>
                      <span className="font-bold text-gray-700">{ftb > 0 ? `${ftbPct.toFixed(1)}%` : '—'}</span>
                    </div>
                    {/* Dual-tone progress bar */}
                    <div className="w-full bg-gray-200 rounded-full h-2.5 overflow-hidden flex">
                      <div
                        className="h-full rounded-full transition-all duration-500"
                        style={{
                          width: `${ftbPct}%`,
                          backgroundColor: brandColor,
                        }}
                      />
                    </div>
                    <div className="mt-2 flex items-center justify-between text-[11px] text-gray-500">
                      <span>{t('operatorBreakdown.inactive')}</span>
                      <span className="font-semibold text-gray-700">{formatNumber(ftbInactive)}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
